import type { CardDef } from './card';
import type { CardEffect } from './effect';
import type { CardTitle, EffectId, PackId } from './ids';
import { namespaceOf } from './ids';
import { hashValue, type JsonValue } from './hash';
import { t } from '../i18n';

/**
 * 一个**数据包**（内容包 / 扩展包）。
 *
 * 想加扩展包时，只需要在 `src/packs/<名字>/` 里导出一个 `ContentPack`，
 * 然后登记到 `src/packs/index.ts`。**核心代码一行都不用改。**
 *
 * `vanilla` 包是原版（56 张），后续可以并列添加：
 * ```
 * vanilla    爆炸猫原版
 * imploding  Imploding Kittens 扩展
 * 群友定制    你们群自己的游戏规则牌
 * ```
 *
 * ⚠️ 叫 `vanilla` 而不是 `core`：以后可能有不依赖原版游戏的牌组，
 * 而「core（核心）」听起来像**引擎的一部分**，描述性不够 ——
 * 这个包只是「原版那一套牌」，不是核心。
 */
export interface ContentPack {
  readonly id: PackId;

  /** 显示名（中文） */
  readonly name: string;

  readonly description: string;

  /** 必需包不能被房主关闭。原版包是必需的。 */
  readonly required?: boolean;

  /**
   * 这个包引入的卡牌。
   * 每张卡的 `id` 必须等于 `${pack.id}:${card.local}`，由注册表校验。
   */
  readonly cards: readonly CardDef[];

  /**
   * 这个包提供的**卡牌效果实现**，key 是 `EffectId`。
   *
   * 核心不认识任何具体效果；卡定义里存 ID，实现从这里查。
   * 每张 `effect !== null` 的牌都**必须**有对应实现，否则注册表启动即报错。
   */
  readonly effects?: Readonly<Record<string, CardEffect>>;
}

/** 牌名 → 放几张。房主的游戏规则可以覆盖它（`docs/rules.md` §11）。 */
export type DeckCounts = Readonly<Record<string, number>>;

export class RegistryError extends Error {
  override readonly name = 'RegistryError';
}

/**
 * **注册表**：把若干数据包合并成一张可查询的表。
 *
 * ⚠️ 它**不是游戏状态**，是静态内容 —— 所以这里用 `Map` 没问题，
 * 不受 `architecture.md` §4「状态里不用 Map」的约束。
 * 一局游戏只需要在 `enabledPacks` 里记下启用了哪些包，
 * 注册表随时可以由包列表重建出来。
 */
export interface CardRegistry {
  readonly packs: readonly ContentPack[];

  /** 所有卡牌，顺序稳定（按包顺序，再按包内声明顺序） */
  readonly cards: readonly CardDef[];

  /** 查不到返回 undefined */
  get(title: CardTitle): CardDef | undefined;

  /** 查不到抛错 —— 用于「这里不可能查不到」的场合 */
  require(title: CardTitle): CardDef;

  byPack(pack: PackId): readonly CardDef[];

  filter(predicate: (card: CardDef) => boolean): readonly CardDef[];

  /** 查一个卡牌效果的实现 */
  effect(id: EffectId): CardEffect | undefined;

  /** 查不到就抛 —— 内容配漏了实现时应当启动即炸 */
  requireEffect(id: EffectId): CardEffect;

  /** 官方（默认）牌堆构成 */
  defaultDeck(): DeckCounts;

  /** 官方牌堆总张数 */
  defaultDeckSize(): number;
}

function assert(condition: unknown, message: string): asserts condition {
  if (!condition) throw new RegistryError(message);
}

/**
 * 合并数据包并校验。
 *
 * 校验项都是「配错内容」这种很难靠肉眼发现的错误，
 * 所以宁可在启动时直接炸掉，也不要拖到联机中途出诡异行为。
 */
export function createRegistry(packs: readonly ContentPack[]): CardRegistry {
  const byId = new Map<CardTitle, CardDef>();
  const cards: CardDef[] = [];
  const seenPacks = new Set<PackId>();
  const effects = new Map<EffectId, CardEffect>();

  for (const pack of packs) {
    assert(!seenPacks.has(pack.id), t('core.registry.duplicatePack', { id: pack.id }));
    seenPacks.add(pack.id);

    for (const card of pack.cards) {
      assert(
        card.pack === pack.id,
        t('core.registry.cardPackMismatch', { id: card.id, declared: card.pack, pack: pack.id }),
      );
      assert(
        card.id === `${pack.id}:${card.local}`,
        t('core.registry.cardIdMismatch', { id: card.id, pack: pack.id, local: card.local }),
      );
      assert(
        namespaceOf(card.id) === pack.id,
        t('core.registry.cardNamespace', { id: card.id, pack: pack.id }),
      );
      assert(!byId.has(card.id), t('core.registry.cardIdDuplicate', { id: card.id }));
      assert(
        Number.isInteger(card.defaultCount) && card.defaultCount >= 0,
        t('core.registry.cardCount', { id: card.id, count: card.defaultCount }),
      );

      byId.set(card.id, card);
      cards.push(card);
    }

    for (const [id, implementation] of Object.entries(pack.effects ?? {})) {
      assert(
        namespaceOf(id) === pack.id,
        t('core.registry.effectNamespace', { id, pack: pack.id }),
      );
      assert(!effects.has(id as EffectId), t('core.registry.effectDuplicate', { id }));
      effects.set(id as EffectId, implementation);
    }
  }

  // 每张声明了效果的牌都必须有实现 —— 漏了就在启动时炸，别拖到玩家打出来那一刻
  for (const card of cards) {
    if (card.effect === null) continue;
    assert(
      effects.has(card.effect),
      t('core.registry.missingEffect', { id: card.id, effect: card.effect }),
    );
  }

  assert(byId.size > 0, t('core.registry.empty'));

  const byPack = new Map<PackId, CardDef[]>();
  for (const card of cards) {
    const list = byPack.get(card.pack);
    if (list) list.push(card);
    else byPack.set(card.pack, [card]);
  }

  const defaultDeckCounts: Record<string, number> = {};
  for (const card of cards) defaultDeckCounts[card.id] = card.defaultCount;

  return {
    packs,
    cards,

    get: (title) => byId.get(title),

    require(title) {
      const card = byId.get(title);
      if (!card) throw new RegistryError(t('core.registry.unknownCard', { title }));
      return card;
    },

    byPack: (pack) => byPack.get(pack) ?? [],

    filter: (predicate) => cards.filter(predicate),

    effect: (id) => effects.get(id),

    requireEffect(id) {
      const implementation = effects.get(id);
      if (!implementation) throw new RegistryError(t('core.registry.missingEffectImpl', { id }));
      return implementation;
    },

    defaultDeck: () => ({ ...defaultDeckCounts }),

    defaultDeckSize: () => cards.reduce((sum, card) => sum + card.defaultCount, 0),
  };
}

/**
 * **内容包的声明式清单** —— 用来算"内容指纹"，给大厅做一致性校验。
 *
 * ⚠️ 只保留**可序列化**的声明部分。`effects` 里装的是函数，
 * `canonicalJson` 会把函数键直接跳过 —— 所以必须改用它们的 **id 列表**，
 * 否则"效果实现换了、卡面元数据一样"的两个包会算出同一个指纹。
 *
 * 边界（哈希到底覆盖什么）是一个**产品决定**：现在覆盖 id / 必需标记 /
 * 名称 / 描述 / 全部卡定义（函数已由 canonicalJson 剔除）/ 效果 id 列表。
 * 想让纯文案改动不算不兼容，就把 `name` / `description` 从这里去掉。
 */
export function packManifest(pack: ContentPack): JsonValue {
  return {
    id: pack.id,
    required: pack.required === true,
    name: pack.name,
    description: pack.description,
    cards: pack.cards as unknown as JsonValue[],
    effectIds: Object.keys(pack.effects ?? {}).sort(),
  };
}

/**
 * **内容包的内容指纹。**
 *
 * 同一份内容 → 同一个 hash；清单里任一字段变了 → 换一个 hash。
 * 用 FNV-1a（见 `core/hash.ts`）：同步、无依赖，够抓"两端内容不一致"。
 */
export function packHash(pack: ContentPack): string {
  return hashValue(packManifest(pack));
}
